import { useEffect } from 'react';
import { PostDocumentUploadResponse } from '@iwoca/lapi-client/edge';
import { Accordion, Pill, Spinner } from '@iwoca/orion';
import { t } from '@lingui/macro';
import clsx from 'clsx';
import { useFileUploadReducer } from 'components/Signup/hooks/useFileUploadReducer';
import { useLocale } from 'hooks/useLocale';
import { TOfferDataRequirement } from 'lendingApi/lapiOverrides.types';
import {
  useGetDocuments,
  useGetIntroducerApplication,
} from 'lendingApi/lendingApiHooks';
import _ from 'lodash';
import { FileStatusGroup } from '../FileStatusGroup/FileStatusGroup';
import { UploadDropButton } from '../UploadDropButton/UploadDropButton';
import styles from './DocumentGroup.module.css';
import {
  TDocumentTypeWithDE,
  FILE_STATUS_GROUPS,
  getInstruction,
  getRequirementsTitle,
  handleDocumentTypeMapping,
} from './DocumentGroupHelper';
import { LinkOpenBankingStatus } from './LinkOpenBankingStatus/LinkOpenBankingStatus';

type TProps = {
  requirement: TOfferDataRequirement['actions'][number];
  isPartnerChannel: boolean;
  isLoading?: boolean;
};

type TDocTypeStatus = {
  status: 'processing' | 'required' | 'completed';
  pillVariant: 'green' | 'london' | 'yellow' | 'red';
  labelText: string;
};

export const DocumentGroup = ({
  requirement,
  isPartnerChannel,
  isLoading,
}: TProps) => {
  const { locale, isUK } = useLocale();
  const { application } = useGetIntroducerApplication();

  const {
    uploadedFiles,
    setAllFiles,
    setFileLoading,
    setFileSuccess,
    setFileError,
    setFileReject,
    removeFile,
  } = useFileUploadReducer();

  const stateKey = application?.customer?.state_key;
  const { data: documents } = useGetDocuments(stateKey);

  // Below logic is mapping DE doc types to uk versions so we upload files to correct categories
  const documentType = handleDocumentTypeMapping(
    requirement.type as TDocumentTypeWithDE,
    locale,
  );

  const mappedRequirement = { ...requirement, type: documentType };

  useEffect(() => {
    if (documents) {
      const documentsForThisDocumentType = documents?.data?.filter(
        (uploadedDocument) => {
          return documentType === uploadedDocument.type;
        },
      );

      setAllFiles(documentsForThisDocumentType);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requirement, documents]);

  const getDocTypeStatus = (): TDocTypeStatus | undefined => {
    switch (mappedRequirement.status) {
      case 'pending':
        return {
          status: 'processing',
          pillVariant: 'london',
          labelText: t`Processing`,
        };
      case 'error':
      case 'required':
        return {
          status: 'required',
          pillVariant: 'red',
          labelText: t`Required`,
        };
      case 'completed':
        return {
          status: 'completed',
          pillVariant: 'green',
          labelText: t`Completed`,
        };
    }
  };

  const documentTypeStatus = getDocTypeStatus();
  const showOpenBanking = mappedRequirement.type === 'bank_statement' && isUK;

  const renderPill = (docType?: TDocTypeStatus) => {
    if (!docType) {
      return null;
    }

    return (
      <Pill
        className={clsx(styles.pill, styles[docType.status])}
        variant={docType.pillVariant}
      >
        {isLoading ? (
          <Spinner className={styles.spinner} size="small" />
        ) : (
          _.capitalize(docType.labelText)
        )}
      </Pill>
    );
  };

  const getDocumentRequirementUploadType = (docType?: string) => {
    if (!docType) {
      return null;
    }

    return docType
      .split('_')
      .join(' ') as PostDocumentUploadResponse['data']['document_type'];
  };

  if (!stateKey) {
    return null;
  }

  return (
    <Accordion
      className={styles.accordion}
      titleClassName={styles.titleContainer}
      title={
        <>
          <p className={styles.title}>
            {getRequirementsTitle(mappedRequirement.type)}
            <span className={styles.optional}>
              {mappedRequirement.is_optional && ' (Optional)'}
            </span>
          </p>
          {!mappedRequirement.is_optional && renderPill(documentTypeStatus)}
        </>
      }
    >
      <>
        {documentTypeStatus?.status === 'required' && (
          <p className={styles.instruction}>
            {getInstruction(
              mappedRequirement.type,
              mappedRequirement.quantifier?.value,
              mappedRequirement.quantifier?.unit,
            )}
          </p>
        )}
        {!isPartnerChannel && (
          <UploadDropButton
            setFileLoading={setFileLoading}
            setFileSuccess={setFileSuccess}
            setFileError={setFileError}
            setFileReject={setFileReject}
            stateKey={stateKey}
            isOptional={mappedRequirement.is_optional}
            documentType={getDocumentRequirementUploadType(documentType)}
          />
        )}
        {FILE_STATUS_GROUPS.map((statusGroup) => {
          return (
            <FileStatusGroup
              key={`${mappedRequirement.type}-${statusGroup}`}
              testId={statusGroup}
              statusGroup={statusGroup}
              files={uploadedFiles}
              removeFile={removeFile}
            />
          );
        })}
        {showOpenBanking && <LinkOpenBankingStatus />}
      </>
    </Accordion>
  );
};
